import { Prose } from '../../components/Prose'
import type { Profile } from '../../lib/metrics'

const KEEP: [string, string, 'good' | 'warn' | 'drop', string][] = [
  ['Protein hedefi', 'Korunur', 'good', 'Kas kaybını engelleyen tek şey. Yoğurt, yumurta, peynir — pişirme gerektirmeyen kaynaklara kay'],
  ['Yürüyüş', 'Korunur, hatta artar', 'good', 'Bebek arabasıyla yürümek zaten yapacağın şey. Huzursuz bebeği yürüyüş sakinleştirir'],
  ['2 öğün düzeni', 'Korunur', 'good', 'Zaten yapıyorsun, kararı azaltıyor. Kaos dönemlerinde az karar = az hata'],
  ['Tenis', "Haftada 1'e düşebilir", 'warn', 'Eşinle önceden takvim yapın. Aşağıdaki nota bak'],
  ['Antrenman 3 gün', "2'ye düşer", 'warn', '25 dakikalık ev antrenmanı bebek uyurken sığar — bu yüzden salon yerine ev seçtik'],
  ['Kalori açığı', 'Bırakılır', 'drop', 'Bölünmüş uykuda açık vermek işe yaramıyor ve moral kırıyor. 3 ay bekle'],
  ['Haftalık tartı', 'Aylığa düşer', 'drop', 'Uyku kaynaklı su tutmayı "kilo aldım" diye okuyup bırakma riskin var'],
]

const FREEZE_OK = [
  'Mercimek / ezogelin çorbası', 'Kuru fasulye, nohut, barbunya',
  'Kıymalı sebze yemekleri (türlü, taze fasulye)', 'Köfte (çiğ ya da pişmiş)',
  'Fırın tavuk parçaları', 'Menemen harcı (soğan-biber-domates kavurması)',
]

const FREEZE_RULES = [
  'Tek porsiyonluk kaplara böl — büyük kap işe yaramaz',
  'Üstüne tarih ve isim yaz (buzdolabı kalemi)',
  'Pilav/bulguru ayrı dondur, yemeğin içinde değil',
  'Salata malzemesi donmaz — o taze kalacak, listeye ekle',
  '3 aydan eskiyi tüket, birikmesin',
]

export function BabyTab({ profile }: { profile: Profile }) {
  const days = profile.dueDate
    ? Math.round((new Date(profile.dueDate).getTime() - Date.now()) / 86_400_000)
    : null
  const portions = days && days > 0 ? Math.floor(days / 7) * 2 : 0

  const color = (k: 'good' | 'warn' | 'drop') =>
    k === 'good' ? 'var(--good-text)' : k === 'drop' ? 'var(--crit)' : 'var(--ink-2)'

  return (
    <>
      <div className="card" style={{ borderLeft: '3px solid var(--s1)' }}>
        <h2>Neden ayrı bir plan gerekiyor</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 14.5, margin: '6px 0 0' }}>
          Bebek geldikten sonra uykun bölünecek. Bölünmüş uykuda açlık hormonu (ghrelin) yükselir,
          tokluk hormonu (leptin) düşer — yani <strong>aynı iradeyle daha fazla yersin ve bunu fark etmezsin</strong>.
          Buna zaman kıtlığı ve stres eklenince o dönemde kilo vermeye çalışmak çoğu insanda başarısız oluyor;
          başarısızlık da genelde planın tamamen bırakılmasıyla bitiyor.
        </p>
        <p style={{ color: 'var(--ink-2)', fontSize: 14.5, margin: '10px 0 0' }}>
          Çözüm: o 3 ayı <strong>baştan bir koruma dönemi olarak planlamak</strong>. Beklentiyi önceden düşürürsen
          kilo vermemek "başarısızlık" değil "plana uygun" olur — ve plandan çıkmazsın.
        </p>
      </div>

      <div className="card">
        <h2>Dondurucu stoğu — bu sekmedeki en değerli şey</h2>
        <Prose className="card-h">
          Uykusuz bir babanın 21:00'de vereceği karar iradeyle ilgili değildir, dolapta ne olduğuyla ilgilidir.
          Sipariş uygulamasını yenecek tek şey, mikrodalgada 4 dakikada hazır olan bir kaptır.
        </Prose>
        <p style={{ fontSize: 14.5, color: 'var(--ink-2)', margin: '0 0 10px' }}>
          Her pazar meal-prep yaparken <strong>fazladan 2 porsiyon</strong> pişirip dondur.
          {portions > 0
            ? <> Doğuma kalan sürede bu <strong>~{portions} porsiyon</strong> eder — yaklaşık bir aylık akşam yemeği güvencesi.</>
            : <> Profil bölümünden doğum tarihini girersen kaç porsiyon biriktirebileceğini hesaplarım.</>}
        </p>
        <div className="grid g2">
          <div className="day">
            <div className="dn">Donduruculuk iyi</div>
            <ul className="tight" style={{ fontSize: 13.5 }}>{FREEZE_OK.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
          <div className="day">
            <div className="dn">Kaplama kuralları</div>
            <ul className="tight" style={{ fontSize: 13.5 }}>{FREEZE_RULES.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Faz 2'de neyi bırakırsın, neyi bırakmazsın</h2>
        <table>
          <thead><tr><th>Şey</th><th>Karar</th><th>Neden</th></tr></thead>
          <tbody>
            {KEEP.map(([what, decision, kind, why]) => (
              <tr key={what}>
                <td><strong>{what}</strong></td>
                <td style={{ color: color(kind) }}>{decision}</td>
                <td style={{ fontVariantNumeric: 'normal' }}>{why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Tenisi korumak istiyorsan şimdi yap</h2>
        <Prose className="card-h">
          Kararlılık tek başına yetmiyor; doğumdan sonra o iki akşam kendiliğinden buharlaşır.
          Şunu bebek gelmeden konuşun:
        </Prose>
        <ul className="tight">
          <li><strong>Takvime sabit blok koyun.</strong> "Salı ve Perşembe 20:00–22:00 Cagatay" diye. Belirsiz bir niyet değil, yazılı bir anlaşma.</li>
          <li><strong>Eşine denk bir blok verin.</strong> Onun da haftada iki kere, aynı uzunlukta, sorgusuz kendi zamanı olsun. Tek taraflı düzenlemeler 3 hafta yaşar.</li>
          <li><strong>Yedek plan belirleyin.</strong> Zor bir gece olduysa o hafta 1'e düşer — bu bir başarısızlık değil, planın parçası.</li>
          <li><strong>İlk 6 hafta muhtemelen gidemezsin</strong>, bunu baştan kabul et. Dersi dondur ya da askıya al.</li>
        </ul>
        <div className="warnbox">
          Bu haftada 2 saatlik bir mesele değil. Yeni babaların kendi rutinini tamamen bırakması yaygın ve
          bir yıl sonra hem kilo hem moral olarak pahalıya patlıyor. İki akşamı korumak bencillik değil, sürdürülebilirlik.
        </div>
      </div>

      <div className="card">
        <h2>Doğumdan önce bitirilecek liste</h2>
        <ul className="tight">
          <li>☐ Dondurucuda 20+ tek porsiyon yemek</li>
          <li>☐ Tenis blokları takvimde, eşinin blokları da</li>
          <li>☐ Tenis dersi için "askıya alma" konuşuldu</li>
          <li>☐ Kan tahlili yapıldı (açlık şekeri, HbA1c, lipid, TSH, D vitamini, B12)</li>
          <li>☐ İki tabak kuralı artık düşünmeden uygulanıyor</li>
          <li>☐ Doğum tarihi panelde girili — faz geçişi otomatik</li>
        </ul>
      </div>
    </>
  )
}
